import React, { useState, useEffect } from 'react';
import { getStatusChipSx } from '../../../constants/statusColors';
import {
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  IconButton,
} from '@mui/material';
import {
  AddOutlined as AddIcon,
  AccountBalance as AuditIcon,
  Security as RiskIcon,
  PersonSearch as NominationIcon,
  AttachMoney as RemunerationIcon,
  Gavel as GovernanceIcon,
  ChevronRightOutlined as ChevronRightIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { csAdminApi } from '../../../services/api';
import { mapCommitteeFromApi, formatDisplayDate, parseMeetingDate, startOfToday } from '../../../utils/apiMappers';

const CommitteesTab = ({ companyId }) => {
  const navigate = useNavigate();
  const [committees, setCommittees] = useState([]);
  const [tablesMissing, setTablesMissing] = useState(false);

  useEffect(() => {
    fetchCommittees();
  }, [companyId]);

  const fetchCommittees = async () => {
    try {
      const res = await csAdminApi.getCommittees({ company_id: companyId });
      setTablesMissing(!!res.data?.tablesMissing);
      const list = (res.data?.data || []).map((c) => {
        const mapped = mapCommitteeFromApi(c);
        const nextDate = parseMeetingDate(mapped.nextMeeting);
        const hasUpcomingMeeting = nextDate && nextDate >= startOfToday();
        return {
          id: mapped.id,
          name: mapped.name,
          type: mapped.type,
          members: mapped.memberCount,
          meetings: mapped.meetingCount,
          nextMeeting: hasUpcomingMeeting ? mapped.nextMeeting : null,
          status: mapped.status,
        };
      });
      setCommittees(list);
    } catch (err) {
      console.error('Committees tab:', err);
      setCommittees([]);
    }
  };

  const getCommitteeIcon = (type) => {
    const iconMap = {
      'Audit': AuditIcon,
      'Risk Management': RiskIcon,
      'Nomination': NominationIcon,
      'Remuneration': RemunerationIcon,
      'Governance': GovernanceIcon,
    };
    return iconMap[type] || AuditIcon;
  };

  const getCommitteeColor = (type) => {
    const colorMap = {
      'Audit': '#1976D2',
      'Risk Management': '#D32F2F',
      'Nomination': '#7B1FA2',
      'Remuneration': '#388E3C',
      'Governance': '#F57C00',
    };
    return colorMap[type] || '#757575';
  };

  const getCommitteeBgColor = (type) => {
    const colorMap = {
      'Audit': '#E3F2FD',
      'Risk Management': '#FFEBEE',
      'Nomination': '#F3E5F5',
      'Remuneration': '#E8F5E9',
      'Governance': '#FFF3E0',
    };
    return colorMap[type] || '#F5F5F5';
  };

  return (
    <Paper sx={{ p: 3 }}>
      {tablesMissing && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Committees tables not found. Run backend/scripts/csadmin/01-committees-schema.sql on Oracle.
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Board Committees</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="small"
          onClick={() => navigate(`/committees/create?companyId=${companyId}`)}
        >
          Create Committee
        </Button>
      </Box>

      {committees.length === 0 ? (
        <Typography variant="body2" color="textSecondary" sx={{ py: 2, textAlign: 'center' }}>
          No committees created yet
        </Typography>
      ) : (
        <List sx={{ p: 0 }}>
          {committees.map((committee, index) => {
            const Icon = getCommitteeIcon(committee.type);
            return (
              <React.Fragment key={committee.id}>
                <ListItem
                  sx={{
                    px: 0,
                    py: 2,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' },
                  }}
                  onClick={() => navigate(`/committees/${committee.id}`)}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '8px',
                      bgcolor: getCommitteeBgColor(committee.type),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mr: 2,
                    }}
                  >
                    <Icon sx={{ fontSize: 28, color: getCommitteeColor(committee.type) }} />
                  </Box>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {committee.name}
                        </Typography>
                        <Chip
                          label={committee.status}
                          sx={{
                            ...getStatusChipSx(committee.status),
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 20,
                            borderRadius: '10px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                          size="small"
                        />
                        {committee.nextMeeting && (
                          <Chip
                            label={`Next: ${formatDisplayDate(committee.nextMeeting)}`}
                            sx={{
                              bgcolor: '#E3F2FD',
                              color: '#1565C0',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 20,
                              borderRadius: '10px',
                            }}
                            size="small"
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                        <Typography variant="body2" color="textSecondary">
                          {committee.type}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          • {committee.members} Members
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          • {committee.meetings} Meetings
                        </Typography>
                      </Box>
                    }
                  />
                  <IconButton size="small">
                    <ChevronRightIcon />
                  </IconButton>
                </ListItem>
                {index < committees.length - 1 && <Divider />}
              </React.Fragment>
            );
          })}
        </List>
      )}
    </Paper>
  );
};

export default CommitteesTab;
